import { useState } from "react";
import type { PointsDiscountState } from "@/pages/checkout/types";

const POINTS_PER_REWARD = 1000;
const REWARD_VALUE_SAR = 100;

interface PointsRedemptionProps {
  availablePoints: number;
  totalPrice: number;
  discountAmount: number;
  pointsDiscount: PointsDiscountState;
  onPointsDiscountChange: (state: PointsDiscountState) => void;
}

export default function PointsRedemption({
  availablePoints,
  totalPrice,
  discountAmount,
  pointsDiscount,
  onPointsDiscountChange,
}: PointsRedemptionProps) {
  const [expanded, setExpanded] = useState(false);

  // Max redeemable: multiples of 1000, can't exceed remaining price after other discounts
  const maxRedeemablePoints = Math.min(
    availablePoints,
    Math.floor((totalPrice - discountAmount) / REWARD_VALUE_SAR) * POINTS_PER_REWARD
  );
  const maxBundles = Math.floor(maxRedeemablePoints / POINTS_PER_REWARD);
  const canRedeem = maxBundles >= 1;

  // Slider: how many bundles to redeem (1 bundle = 1000 pts = 100 SAR)
  const [bundles, setBundles] = useState(maxBundles);

  const selectedPoints = bundles * POINTS_PER_REWARD;
  const selectedSAR = bundles * REWARD_VALUE_SAR;

  const handleApply = () => {
    onPointsDiscountChange({
      pointsToRedeem: selectedPoints,
      discountAmount: selectedSAR,
      applied: true,
    });
  };

  const handleRemove = () => {
    onPointsDiscountChange({ pointsToRedeem: 0, discountAmount: 0, applied: false });
    setBundles(maxBundles);
  };

  // Not enough points
  if (availablePoints < POINTS_PER_REWARD) {
    const needed = POINTS_PER_REWARD - availablePoints;
    return (
      <div className="rounded-2xl border border-white/8 bg-[#111] px-4 py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-amber-400/10 rounded-xl flex-shrink-0">
            <i className="ri-vip-crown-line text-amber-400 text-lg"></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>
              نقاط المكافآت
            </p>
            <p className="text-gray-500 text-xs mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
              لديك <span className="text-amber-400 font-black">{availablePoints}</span> نقطة —
              تحتاج <span className="text-gray-300 font-bold">{needed}</span> نقطة إضافية للاستبدال
            </p>
          </div>
          <div className="flex-shrink-0 bg-[#1a1a1a] border border-white/8 rounded-full px-3 py-1">
            <span className="text-gray-500 text-[11px] font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>
              {availablePoints}/{POINTS_PER_REWARD}
            </span>
          </div>
        </div>
        {/* Mini progress */}
        <div className="mt-3 h-1.5 bg-white/5 rounded-full overflow-hidden">
          <div
            className="h-full bg-amber-400/40 rounded-full transition-all"
            style={{ width: `${Math.min(100, (availablePoints / POINTS_PER_REWARD) * 100)}%` }}
          ></div>
        </div>
      </div>
    );
  }

  // Applied state
  if (pointsDiscount.applied) {
    return (
      <div className="rounded-2xl border-2 border-amber-400/40 bg-amber-400/8 px-4 py-4 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-amber-400/0 via-amber-400/60 to-amber-400/0"></div>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-amber-400/20 rounded-xl flex-shrink-0">
            <i className="ri-vip-crown-fill text-amber-400 text-lg"></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-amber-400 text-sm font-black" style={{ fontFamily: "'Cairo', sans-serif" }}>
              تم تطبيق نقاط المكافآت
            </p>
            <p className="text-amber-400/70 text-xs mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
              استبدلت <span className="font-black text-amber-400">{pointsDiscount.pointsToRedeem.toLocaleString()}</span> نقطة
              بخصم <span className="font-black text-green-400">{pointsDiscount.discountAmount.toLocaleString()} ر.س</span>
            </p>
          </div>
          <button
            type="button"
            onClick={handleRemove}
            className="flex-shrink-0 flex items-center gap-1.5 bg-white/5 hover:bg-red-400/10 border border-white/10 hover:border-red-400/30 text-gray-400 hover:text-red-400 text-xs font-bold px-3 py-2 rounded-xl transition-all cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            <i className="ri-close-line text-sm"></i>
            إلغاء
          </button>
        </div>

        {/* Remaining points info */}
        <div className="mt-3 flex items-center gap-2 bg-black/20 rounded-xl px-3 py-2">
          <i className="ri-information-line text-amber-400/60 text-xs flex-shrink-0"></i>
          <p className="text-amber-400/60 text-[11px]" style={{ fontFamily: "'Cairo', sans-serif" }}>
            سيتبقى لك <span className="font-black text-amber-400/80">{(availablePoints - pointsDiscount.pointsToRedeem).toLocaleString()}</span> نقطة بعد هذا الطلب
          </p>
        </div>
      </div>
    );
  }

  // Available to redeem
  return (
    <div className="rounded-2xl border border-amber-400/25 bg-[#111] overflow-hidden">
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-4 cursor-pointer hover:bg-amber-400/5 transition-colors"
      >
        <div className="w-10 h-10 flex items-center justify-center bg-amber-400/15 rounded-xl flex-shrink-0">
          <i className="ri-vip-crown-line text-amber-400 text-lg"></i>
        </div>
        <div className="flex-1 min-w-0 text-right">
          <p className="text-white text-sm font-black" style={{ fontFamily: "'Cairo', sans-serif" }}>
            استبدل نقاطك بخصم
          </p>
          <p className="text-gray-400 text-xs mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
            لديك <span className="text-amber-400 font-black">{availablePoints.toLocaleString()}</span> نقطة —
            يمكن استبدال <span className="text-green-400 font-black">{(maxBundles * REWARD_VALUE_SAR).toLocaleString()} ر.س</span> خصم
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="bg-green-400/15 border border-green-400/25 text-green-400 text-[11px] font-black px-2.5 py-1 rounded-full whitespace-nowrap" style={{ fontFamily: "'Cairo', sans-serif" }}>
            وفّر {(maxBundles * REWARD_VALUE_SAR).toLocaleString()} ر.س
          </span>
          <i className={`${expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-gray-400 text-lg`}></i>
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-amber-400/15 px-4 py-4 bg-amber-400/3">
          {/* Points info */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="bg-[#1a1a1a] rounded-xl p-3 text-center border border-white/5">
              <p className="text-amber-400 text-base font-black" style={{ fontFamily: "'Cairo', sans-serif" }}>
                {availablePoints.toLocaleString()}
              </p>
              <p className="text-gray-600 text-[10px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>نقطة متاحة</p>
            </div>
            <div className="bg-[#1a1a1a] rounded-xl p-3 text-center border border-white/5">
              <p className="text-green-400 text-base font-black" style={{ fontFamily: "'Cairo', sans-serif" }}>
                {selectedSAR.toLocaleString()} ر.س
              </p>
              <p className="text-gray-600 text-[10px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>خصم مختار</p>
            </div>
            <div className="bg-[#1a1a1a] rounded-xl p-3 text-center border border-white/5">
              <p className="text-gray-300 text-base font-black" style={{ fontFamily: "'Cairo', sans-serif" }}>
                {(availablePoints - selectedPoints).toLocaleString()}
              </p>
              <p className="text-gray-600 text-[10px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>يتبقى</p>
            </div>
          </div>

          {/* Slider — only show if more than 1 bundle available */}
          {maxBundles > 1 && (
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-400 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  عدد الحزم: <span className="text-white font-black">{bundles}</span>
                  <span className="text-gray-600 mr-1">(كل حزمة = 1000 نقطة = 100 ر.س)</span>
                </span>
              </div>
              <input
                type="range"
                min={1}
                max={maxBundles}
                value={bundles}
                onChange={(e) => setBundles(Number(e.target.value))}
                className="w-full accent-amber-400 cursor-pointer"
              />
              <div className="flex justify-between text-[10px] text-gray-600 mt-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                <span>100 ر.س</span>
                <span>{(maxBundles * REWARD_VALUE_SAR).toLocaleString()} ر.س</span>
              </div>
            </div>
          )}

          {/* Rule */}
          <div className="flex items-center gap-2 bg-[#1a1a1a] rounded-xl px-3 py-2 mb-4 border border-white/5">
            <i className="ri-information-line text-amber-400/60 text-xs flex-shrink-0"></i>
            <p className="text-gray-500 text-[11px]" style={{ fontFamily: "'Cairo', sans-serif" }}>
              1000 نقطة = 100 ر.س خصم · النقاط تُخصم فور تأكيد الطلب
            </p>
          </div>

          {/* Apply button */}
          <button
            type="button"
            onClick={handleApply}
            disabled={!canRedeem}
            className="w-full flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 disabled:opacity-50 disabled:cursor-not-allowed text-black font-black py-3 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            <i className="ri-gift-2-line text-base"></i>
            استبدل {selectedPoints.toLocaleString()} نقطة بخصم {selectedSAR.toLocaleString()} ر.س
          </button>
        </div>
      )}
    </div>
  );
}
